import { ExpandMore, InfoOutlined } from '@mui/icons-material';
import {
  Collapse,
  IconButton,
  List,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { upperFirst } from 'es-toolkit/compat';
import { useState } from 'react';

import { TimeslotOrderGroupModel } from '@merchant-clients/order';

import { ViewSection } from '@frontend/components';
import { useLocale } from '@frontend/hooks';
import { tOrder } from '@frontend/locales';

import { OrderListItem } from './order-list-item.component';
import { formatTimeslotHeader } from './order-timeslot-header.utils';
import {
  getTimeslotGroupKey,
  resolveTimeslotExpandedState,
} from './order-timeslot-list.utils';

export interface OrderTimeslotListProps {
  groups: TimeslotOrderGroupModel[];
}

export const OrderTimeslotList = ({ groups }: OrderTimeslotListProps) => {
  const theme = useTheme();
  const { formatters } = useLocale();
  const [expandedByKey, setExpandedByKey] = useState(() =>
    resolveTimeslotExpandedState(groups, {}),
  );
  const [previousGroups, setPreviousGroups] = useState(groups);

  const todayLabel = upperFirst(formatters.relativeDay.format(0, 'day'));

  // New groups keep the expanded state of the groups that remain.
  if (groups !== previousGroups) {
    setPreviousGroups(groups);
    setExpandedByKey((previous) =>
      resolveTimeslotExpandedState(groups, previous),
    );
  }

  const toggleGroup = (key: string) => {
    setExpandedByKey((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  return (
    <>
      {groups.map((group, index) => {
        const groupKey = getTimeslotGroupKey(group);
        const headerText = formatTimeslotHeader(group, todayLabel);
        const expanded = expandedByKey[groupKey];
        const ordersId = `timeslot-orders-${groupKey}`;

        return (
          <ViewSection
            fullWidth
            sx={{
              p: 3,
              backgroundColor:
                index % 2 === 0 ? undefined : theme.palette.action.hover,
              height:
                expanded && groups.length === index + 1 ? '100%' : undefined,
            }}
            key={groupKey}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              mb={expanded ? 0 : undefined}
              onClick={() => toggleGroup(groupKey)}
              sx={{ cursor: 'pointer' }}
            >
              <IconButton
                aria-expanded={expanded}
                aria-controls={ordersId}
                aria-label={headerText}
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleGroup(groupKey);
                }}
              >
                <ExpandMore
                  sx={{
                    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: theme.transitions.create('transform', {
                      duration: theme.transitions.duration.shortest,
                    }),
                  }}
                />
              </IconButton>
              <Typography variant="h6">
                {headerText} ({group.orders.length})
              </Typography>
              {group.truncated && (
                <Tooltip title={tOrder.orderList.timeslotTruncated()}>
                  <InfoOutlined
                    fontSize="small"
                    onClick={(event) => event.stopPropagation()}
                    sx={{ color: theme.palette.warning.main }}
                  />
                </Tooltip>
              )}
            </Stack>
            <Collapse in={expanded} timeout="auto">
              <List
                id={ordersId}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'flex-start',
                  minHeight: '170px',
                }}
              >
                {group.orders.map((order) => (
                  <OrderListItem key={order.id} order={order} />
                ))}
              </List>
            </Collapse>
          </ViewSection>
        );
      })}
    </>
  );
};
